import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ShellOutletErrorBoundaryProps {
  children: ReactNode;
}

interface ShellOutletErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
  isChunkLoadError: boolean;
}

function isChunkLoadErrorMessage(message: string | null | undefined): boolean {
  if (!message) return false;
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk [\d]+ failed/i.test(
    message
  );
}

/**
 * Isolates routed page crashes (e.g. DevExtreme #185) so header/nav stay usable.
 * Chunk-load failures need a full page reload (React.lazy caches the rejection) — but
 * only when the user clicks Reload. Auto-reload races OIDC login and causes auth loops.
 */
export class ShellOutletErrorBoundary extends Component<
  ShellOutletErrorBoundaryProps,
  ShellOutletErrorBoundaryState
> {
  state: ShellOutletErrorBoundaryState = {
    hasError: false,
    errorMessage: null,
    isChunkLoadError: false
  };

  static getDerivedStateFromError(error: Error): ShellOutletErrorBoundaryState {
    const errorMessage = error?.message?.trim() || 'Something went wrong rendering this page.';
    return {
      hasError: true,
      errorMessage,
      isChunkLoadError: isChunkLoadErrorMessage(errorMessage)
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (typeof console !== 'undefined') {
      console.error('[shell] outlet crashed; nav kept usable', error, info.componentStack);
    }
  }

  private reset = () => {
    if (this.state.isChunkLoadError) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, errorMessage: null, isChunkLoadError: false });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="shell-outlet-error" role="alert">
        <h2 className="shell-outlet-error__title">
          {this.state.isChunkLoadError ? 'This view failed to load' : 'This view crashed'}
        </h2>
        <p className="shell-outlet-error__message">
          {this.state.isChunkLoadError
            ? 'A newer deploy may have replaced this page bundle. Use Reload page once, then try again.'
            : (this.state.errorMessage ?? 'Something went wrong rendering this page.')}
        </p>
        <p className="shell-outlet-error__hint">
          Use the navigation menu to open another page, or reload this view.
        </p>
        <button type="button" className="shell-outlet-error__reload" onClick={this.reset}>
          {this.state.isChunkLoadError ? 'Reload page' : 'Reload this view'}
        </button>
      </div>
    );
  }
}
